import { useAPI } from '@providers/useAPI';
import type { TAdminUserDetails } from 'app/pages/Admin/UserDetailsPage/types/TAdminUserDetails';

import { createContext, ReactNode, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { useAdminUserDetailsQueries } from '../hooks/useAdminUserDetailsQueries';

type TState = {
  user?: TAdminUserDetails;
  domainErrors: string[];
  isLoading: boolean;
  hasInitialError: boolean;
  isFetching: boolean;
  retryUser: (domain?: string) => void;
  fetchData: () => void;
};

const PageContext = createContext<TState>({
  user: undefined,
  domainErrors: [],
  isLoading: false,
  hasInitialError: false,
  isFetching: false,
  retryUser: () => {},
  fetchData: () => {},
});

type TProps = {
  children: ReactNode;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const { userId = '' } = useParams<{ userId: string }>();

  const userQuery = useAdminUserDetailsQueries(call, userId);

  const user = userQuery.user;
  const isLoading = userQuery.isLoading;
  const hasInitialError = userQuery.hasInitialError;

  const fetchData = () => {
    void userQuery.refetch();
  };

  const retryUser = (domain?: string) => {
    if (domain) {
      void userQuery.failedDomains.find(([name]) => name === domain)?.[1].refetch();
      return;
    }
    void userQuery.refetch();
  };

  return (
    <PageContext.Provider
      value={{
        user,
        domainErrors: userQuery.failedDomains.map(([domain]) => domain),
        isLoading,
        hasInitialError,
        isFetching: userQuery.isFetching,
        retryUser,
        fetchData,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
